<template>
  <section id="savedRFQ">
    <!-- ===== PER-QUOTE PRINT-ONLY HEADERS =====
         The letterhead (logo + Parts Department address) used
         to live here as a single page-level banner. With
         multi-quote print support, each Quote # group now
         carries its own copy of the header inside the
         v-for("group in groupedOrders") loop further down,
         and a `page-break-before: always` rule on subsequent
         groups gives every quote its own page in print. The
         per-quote header markup is identical to what used to
         render here, just relocated. See the
         `.print-only-header` block inside the v-for and the
         @media print rules at the bottom of this file. -->

    <!-- The screen-rendered logo error fallback flag is
         consumed by every per-quote header instance below
         (single piece of state, multiple consumers). -->

    <div id="breadcrumb">
      <div class="container">
        <ul>
          <li><router-link to="/" tag="a">Home</router-link></li>
          <li class="last">
            <router-link to="#" tag="a">Saved Quote</router-link>
          </li>
        </ul>
      </div>
    </div>
    <div id="finalizeTop">
      <div class="container">
        <div class="row mb-5">
          <div class="col-12 mb-3">
            <h1>Thank you for the opportunity, your quote is saved!</h1>
          </div>
          <div class="col-12">
            <h4>You can find this quote and any previously saved quotes <router-link to="/account/savedcarts" tag="a">here.</router-link></h4>
          </div>
        </div>
        <div class="row mb-5">
          <div class="col-12">
            <h2>Quote #{{ orderId }}</h2>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== PRINT QUOTE ACTION =====
         Centered Print button in its own breathing-room band
         between the heading and the quote detail blocks.
         Opens the Print Quote(s) modal — see #print-quote-modal
         below. The modal lets the user confirm the action and,
         when more than one quote is present, pick which subset
         to print. The modal's confirm handler is what actually
         calls window.print() (the user picks "Save as PDF" from
         the destination dropdown if they want a PDF).
         The @media print stylesheet hides the site chrome, the
         Place Order / nav action buttons, and this section
         itself so the printout is just the quote receipt
         content matching the on-screen page. Replaces the
         legacy "Download PDF Version of Your Quote" anchor
         link. -->
    <div class="print-order-band no-print">
      <div class="container">
        <button type="button"
                class="btn btn-outline-dark btn-print-order"
                @click="openPrintModal">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
               viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
               class="btn-print-icon">
            <polyline points="6 9 6 2 18 2 18 9"></polyline>
            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
            <rect x="6" y="14" width="12" height="8"></rect>
          </svg>
          {{ printButtonLabel }}
        </button>
      </div>
    </div>

    <!-- ===== GROUPED QUOTES (by Quote Reference → Machine + Assembly) =====
         Same visual treatment as Cart and OrderDetails. Each Quote
         Reference is one "Quote #N" group; within it, each unique
         Machine + Assembly combo is a card with collapsible parts. Parts
         duplicated across multiple backing PurchaseOrder records under
         the same Quote Reference + machine + assembly are merged so the
         user sees one row per part code with summed quantities and cost.
         The view is read-only — no edits, no add/remove, no quantity
         changes (matches the original disabled-input behavior). -->
    <b-form id="assemblyForm">
      <!-- ===== PER-QUOTE SECTIONS =====
           Each Quote # group is wrapped in a `print-quote-section`
           so the print stylesheet can:
             1. Insert a `page-break-before: always` between
                consecutive sections — every quote starts on its
                own page in the printed output.
             2. Hide a section entirely when the user picked a
                subset to print (printingSelectedOnly flag set,
                this group's number not in selectedQuotesForPrint).
                The `print-quote-hidden` class drives that via
                an @media print { display: none } rule.
           Outside print (default screen view) the wrapper is
           a transparent passthrough — no visual change. -->
      <section class="print-quote-section"
               v-for="(group, groupIndex) in groupedOrders"
               :key="'qr-' + group.number"
               :class="{ 'print-quote-hidden': isQuoteHiddenForPrint(group.number) }">
        <!-- ===== PER-QUOTE PRINT-ONLY LETTERHEAD =====
             Identical markup and behavior to the legacy
             page-level header, just relocated so each quote
             carries its own copy. Hidden on screen via the
             scoped .print-only-header { display: none } rule;
             revealed in print by the @media print { display:
             flex } rule at the bottom of the file.

             Two logo strategies stacked: <img> first (same
             SVG used by Head.vue), text "STEELE" fallback if
             the SVG fails to load. printLogoFailed is shared
             across every header instance — one failed load
             flips the flag for all of them, which is the
             correct behavior since they all reference the
             same asset. -->
        <div class="print-only-header">
          <div class="print-only-header-logo">
            <img src="@/assets/img/steele-logo.svg"
                 alt="JC Steele"
                 class="print-only-header-logo-img"
                 @error="onPrintLogoError"
                 v-if="!printLogoFailed" />
            <div class="print-only-header-logo-text"
                 v-if="printLogoFailed">
              STEELE
            </div>
          </div>
          <div class="print-only-header-address">
            <!-- Doc identifier (Order #, Quote #, or
                 Acknowledgement #) — print-only, renders at
                 the top of every printed page so the
                 recipient can tie the output back to a
                 specific record. -->
            <div class="print-only-header-doc-id">Quote # {{ orderId }}</div>
            <div class="print-only-header-address-name">Parts Department</div>
            <div>145 National Dr.</div>
            <div>Statesville, NC 28625</div>
            <div>P. (800) 278-3353</div>
          </div>
        </div>

        <div class="container">
          <div class="po-group">
            <!-- Quote header — Quote Reference IS the label.
                 The reference also shows on the subtotal row
                 inside the first assembly card; the duplication
                 is intentional so each printed page (one quote
                 per page) reads cleanly as "Quote #100" with
                 the reference number visible at both the top
                 banner and the subtotal callout. -->
            <div class="order-header">
              <span class="order-header-label">
                Purchase Order #{{ group.number }}
              </span>
              <span class="order-header-meta">
                {{ group.machineGroups.length }}
                {{ group.machineGroups.length === 1 ? 'assembly' : 'assemblies' }}
              </span>
            </div>

            <!-- One card per Machine + Assembly combo within this Quote Ref. -->
            <div class="assembly-card"
                 v-for="(mg, mgIndex) in group.machineGroups"
                 :key="'asm-' + group.number + '-' + mg.key">
              <div class="assembly-card-header">
                <div class="assembly-card-heading">
                  <div class="assemblyName">{{ mg.machineName }}</div>
                  <div class="assemblyDescription">{{ mg.assemblyName }}</div>
                </div>
                <!-- Quote-ref subtotal in the FIRST assembly card's header.
                     Read-only — no inline edit on a saved quote. -->
                <div class="assembly-card-po-subtotal" v-if="mgIndex === 0">
                  <div class="assembly-card-po-label">
                    Subtotal for Quote Reference:
                    <span class="subtotal-po">{{ group.number || '—' }}</span>
                  </div>
                  <div class="assembly-card-po-amount" v-if="group.cost > 0">
                    {{ group.cost | currency }}
                  </div>
                  <div class="assembly-card-po-amount" v-else>$0.00</div>
                </div>
              </div>

              <!-- Collapsible summary row: toggle + code×qty chips + subtotal -->
              <div class="assembly-summary"
                   :class="{ expanded: isGroupExpanded(group.number, mg.key) }"
                   @click="toggleGroup(group.number, mg.key)">
                <div class="assembly-toggle"
                     :class="{ expanded: isGroupExpanded(group.number, mg.key) }"
                     :aria-label="isGroupExpanded(group.number, mg.key) ? 'Collapse' : 'Expand'">
                  <span class="assembly-toggle-icon">{{ isGroupExpanded(group.number, mg.key) ? '−' : '+' }}</span>
                </div>
                <div class="assembly-summary-content">
                  <span class="assembly-summary-count">
                    {{ mg.mergedParts.length }} {{ mg.mergedParts.length === 1 ? 'part' : 'parts' }}
                  </span>
                  <span class="code-chip"
                        v-for="p in mg.mergedParts"
                        :key="'chip-' + group.number + '-' + mg.key + '-' + (p.part && p.part.code)">
                    {{ p.part && p.part.code }}<span class="code-qty">×{{ p.quantity }}</span>
                  </span>
                  <span class="assembly-summary-empty" v-if="mg.mergedParts.length === 0">
                    No parts on this assembly.
                  </span>
                </div>
                <div class="assembly-summary-total"
                     v-if="group.machineGroups.length > 1">
                  <!-- This per-assembly total is suppressed when
                       the PO has only one assembly, because in
                       that case it equals the PO subtotal already
                       shown above (.assembly-card-po-subtotal)
                       and the duplicate read as visual noise on
                       single-assembly receipts. With more than
                       one assembly, each assembly's own subtotal
                       IS distinct from the PO grand total, so
                       the breakdown stays useful. -->
                  <span v-if="mg.cost > 0">{{ mg.cost | currency }}</span>
                  <span v-else>$0.00</span>
                </div>
              </div>

              <!-- Parts table — read-only, collapsed by default. -->
              <div class="assembly-table-wrap" v-show="isGroupExpanded(group.number, mg.key)">
                <b-table stacked="sm" :fields="partsFields" :items="mg.mergedParts" responsive="sm">
                  <template v-slot:cell(item)="data">
                    <div class="nowrap">{{ data.item.part && data.item.part.item }}</div>
                  </template>
                  <template v-slot:cell(code)="data">
                    <div class="nowrap">{{ data.item.part && data.item.part.code }}</div>
                  </template>
                  <template v-slot:cell(description)="data">
                    <div class="text-left">{{ data.item.part && data.item.part.description }}</div>
                  </template>
                  <template v-slot:cell(qty)="data">
                    {{ data.item.part && data.item.part.quantityPerMachine }}
                  </template>
                  <template v-slot:cell(price)="data">
                    {{ (data.item.part && data.item.part.price) | currency }}
                  </template>
                  <template v-slot:cell(quoteqty)="data">
                    {{ data.item.quantity }}
                  </template>
                  <template v-slot:cell(cost)="data">
                    {{ data.item.cost | currency }}
                  </template>
                </b-table>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ===== QUOTE GRAND TOTAL =====
           Mirrors Cart.vue's grand-total pattern: clean white card with
           brand-red value and a black top accent. Replaces the old
           dark machinery-image banner.

           no-print is bound conditionally based on groupedOrders.length:
             - 1 quote  → cross-document grand total prints (it's the
                          same as the only quote's subtotal anyway).
             - N quotes → cross-document grand total is suppressed in
                          print. Each printed page already shows that
                          quote's own subtotal in its first assembly
                          card's header ("Subtotal for Quote
                          Reference: 100"), so a cross-document total
                          trailing the last printed page would just
                          confuse rather than inform. The total stays
                          visible on screen — only the print output
                          loses it. -->
      <div class="container" v-if="orders && orders.length > 0">
        <div class="cart-grand-total"
             :class="{ 'no-print': groupedOrders.length > 1 }">
          <div class="cart-grand-total-label">Quote Total</div>
          <div class="cart-grand-total-value">{{ total | currency }}</div>
        </div>

        <!-- Four primary actions, side-by-side:
               1. Place Order — convert this saved quote into an actual
                                order, navigating into the Cart route
                                with the quote's purchase orders.
               2. Check Order Status — utility nav (was sidebar)
               3. Create New Order — utility nav (was sidebar)
               4. Create New Quote — utility nav (was sidebar)
             Place Order is filled red as the primary commit; the three
             relocated nav buttons are dark to indicate secondary
             actions. no-print so they don't appear in the printed
             quote receipt. -->
        <div class="cart-finalize-actions no-print">
          <a @click="placeOrder()" class="btn btn-primary">Place Order</a>
          <router-link to="/account/savedcarts?tab=orders" tag="a" class="btn btn-dark"
            >Check Order Status</router-link
          >
          <router-link to="/order/create" tag="a" class="btn btn-dark"
            >Create New Order</router-link
          >
          <router-link to="/rfq/create" tag="a" class="btn btn-dark"
            >Create New Quote</router-link
          >
        </div>
      </div>
    </b-form>

    <!-- ===== PRINT QUOTE(S) MODAL =====
         Opens when the user clicks the Print Quote(s) button
         in the .print-order-band above. Always shown on click —
         even for the single-quote case — so the user has a
         consistent confirmation step that mirrors the
         Finalize / Print Selected Quotes flow on
         /cart/rfqsaveditems.

         Two flavors driven off groupedOrders.length:
           1 quote   — banner reads "Ready to Print Your Quote?",
                       no checkboxes (one row, always selected),
                       primary button reads "Print Quote".
           N quotes  — banner reads "Ready to Print Quotes?",
                       a checkbox per quote so the user can
                       deselect ones they don't want to print,
                       primary button reads "Print All Quotes"
                       when everything's selected, "Print N
                       Selected Quotes" when a subset is.

         no-print on the modal-class so an accidentally-open
         dialog doesn't bleed into the print output. -->
    <b-modal id="print-quote-modal"
             v-model="showPrintModal"
             :title="printModalTitle"
             size="md"
             centered
             modal-class="print-quote-modal no-print"
             :ok-title="printModalConfirmLabel"
             ok-variant="primary"
             cancel-title="Cancel"
             cancel-variant="outline-secondary"
             :ok-disabled="!hasSelectedQuotesForPrint"
             @ok.prevent="confirmPrint">

      <!-- Brand-red banner — same visual treatment as the
           Modify Quote / Finalize modals on /cart/rfqsaveditems
           so the two flows feel consistent. Title varies by
           single vs multi-quote case (see printModalBannerTitle
           computed). -->
      <div class="modify-restored-banner print-quote-banner">
        <div class="modify-restored-title">
          {{ printModalBannerTitle }}
        </div>
        <div class="modify-restored-body">
          <span v-if="groupedOrders.length === 1">
            Confirm below — then click <strong>Print Quote</strong>
            to open the printable quote receipt.
          </span>
          <span v-else>
            Confirm the quotes below — then click
            <strong>{{ printModalConfirmLabel }}</strong> to open
            the printable quote receipt. Each selected quote
            prints on its own page with the JC Steele
            letterhead at the top.
          </span>
        </div>
      </div>

      <p class="convert-modal-intro" v-if="groupedOrders.length > 1">
        Select which quotes you want to include on the printed
        output.
      </p>

      <!-- Quote rows. For a single quote we still render the
           row (just without a selection checkbox) so the user
           sees what they're about to print. For multiple quotes,
           each row gets a b-form-checkbox bound by the quote
           number — same container/styling vocabulary
           (.convert-quote-list, .convert-quote-row,
           .convert-quote-row-grouped, etc.) used by the Finalize
           / Save for Later picker on /cart/rfqsaveditems so the
           two pages feel like siblings.

           Per-row content mirrors the on-page layout:
           Quote #{number}, assembly count, total cost, and a
           small list of each constituent assembly. -->
      <div class="convert-quote-list">
        <div v-for="(group, groupIndex) in groupedOrders"
             :key="'print-pick-' + group.number"
             class="convert-quote-row convert-quote-row-grouped">
          <!-- Multi-quote: bound checkbox per row. -->
          <b-form-checkbox v-if="groupedOrders.length > 1"
                           :checked="isQuoteSelectedForPrint(group.number)"
                           @change="setQuoteSelectionForPrint(group.number, $event)">
            <div class="convert-quote-row-main">
              <strong class="convert-quote-row-label">
                Purchase Order #{{ group.number }}
              </strong>
            </div>
            <div class="convert-quote-row-sub">
              {{ group.machineGroups.length }}
              {{ group.machineGroups.length === 1 ? 'assembly' : 'assemblies' }}
              <span class="convert-quote-row-amount"
                    v-if="group.cost > 0">
                · {{ group.cost | currency }}
              </span>
            </div>
            <div class="convert-quote-row-assemblies"
                 v-if="group.machineGroups.length > 0">
              <div v-for="mg in group.machineGroups"
                   :key="'print-pick-asm-' + group.number + '-' + mg.key"
                   class="convert-quote-row-assembly">
                <span v-if="mg.machineName">{{ mg.machineName }}</span>
                <template v-if="mg.assemblyName"> · {{ mg.assemblyName }}</template>
                <template v-if="mg.mergedParts && mg.mergedParts.length">
                  · {{ mg.mergedParts.length }}
                  {{ mg.mergedParts.length === 1 ? 'part' : 'parts' }}
                </template>
              </div>
            </div>
          </b-form-checkbox>

          <!-- Single-quote: no checkbox, just the summary so
               the user can see what they're about to print
               before confirming. -->
          <div v-else class="print-quote-row-static">
            <div class="convert-quote-row-main">
              <strong class="convert-quote-row-label">
                Purchase Order #{{ group.number }}
              </strong>
            </div>
            <div class="convert-quote-row-sub">
              {{ group.machineGroups.length }}
              {{ group.machineGroups.length === 1 ? 'assembly' : 'assemblies' }}
              <span class="convert-quote-row-amount"
                    v-if="group.cost > 0">
                · {{ group.cost | currency }}
              </span>
            </div>
            <div class="convert-quote-row-assemblies"
                 v-if="group.machineGroups.length > 0">
              <div v-for="mg in group.machineGroups"
                   :key="'print-static-asm-' + group.number + '-' + mg.key"
                   class="convert-quote-row-assembly">
                <span v-if="mg.machineName">{{ mg.machineName }}</span>
                <template v-if="mg.assemblyName"> · {{ mg.assemblyName }}</template>
                <template v-if="mg.mergedParts && mg.mergedParts.length">
                  · {{ mg.mergedParts.length }}
                  {{ mg.mergedParts.length === 1 ? 'part' : 'parts' }}
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Select-all / deselect-all toggle — only when there's
           more than one quote (single-quote case has no
           checkbox to flip anyway). Mirrors the Finalize modal
           on /cart/rfqsaveditems for visual consistency. -->
      <div class="convert-quote-toggle-all"
           v-if="groupedOrders.length > 1">
        <a href="#"
           @click.prevent="toggleSelectAllQuotesForPrint">
          {{ selectedQuotesForPrint.length === groupedOrders.length
              ? 'Deselect All'
              : 'Select All' }}
        </a>
      </div>
    </b-modal>
  </section>
</template>

<script>
import FeApiService from "@/assets/js/FeApiService";
const apiService = new FeApiService();

import "expose-loader?$!expose-loader?jQuery!jquery";
import "semantic-ui-dropdown/dropdown.js";

import Vue from "vue";
import Vue2Filters from "vue2-filters";

Vue.use(Vue2Filters);

export default {
  name: "SavedRFQ",
  computed: {
    // Quotes grouped two levels deep, mirroring Cart and OrderDetails:
    //   1. By Quote Reference (top level — one "Quote #N" block each)
    //   2. Within each reference, by Machine + Assembly combination
    // Within each subgroup, parts are deduplicated by code (quantities
    // and costs summed). Read-only here — no _sources tracking since
    // nothing on this page can be updated/removed.
    groupedOrders() {
      return this.buildGroupedOrders(this.orders);
    },

    // ===== PRINT BUTTON / MODAL TEXT (singular vs plural) =====
    // Drives the visible label on the .btn-print-order button
    // and the modal title. Singular / plural based purely on
    // groupedOrders.length so a one-quote saved RFQ reads
    // naturally instead of "Print Quotes" with one item.
    printButtonLabel() {
      return this.groupedOrders.length > 1 ? "Print Quotes" : "Print Quote";
    },
    printModalTitle() {
      return this.groupedOrders.length > 1 ? "Print Quotes" : "Print Quote";
    },

    // ===== BANNER TITLE =====
    // The big red callout at the top of the modal. Singular
    // form for the one-quote case — "Ready to Print Your
    // Quote?" — matching the wording the requesting party
    // specified. Plural form covers the more common
    // multi-Quote-Reference case.
    printModalBannerTitle() {
      return this.groupedOrders.length > 1
        ? "Ready to Print Quotes?"
        : "Ready to Print Your Quote?";
    },

    // ===== CONFIRM BUTTON LABEL =====
    // Three flavors:
    //   - Single quote                                 → "Print Quote"
    //   - Multi quote, all selected                    → "Print All Quotes"
    //   - Multi quote, partial selection               → "Print N Selected Quotes"
    // Matches the dynamic-label pattern the Finalize /
    // Print modal uses on /cart/rfqsaveditems so the user
    // gets useful feedback about what the action is going
    // to do.
    printModalConfirmLabel() {
      var total = this.groupedOrders.length;
      if (total <= 1) return "Print Quote";
      var picked = this.selectedQuotesForPrint.length;
      if (picked === 0) return "Print";
      if (picked === total) return "Print All Quotes";
      return "Print " + picked + " Selected " +
        (picked === 1 ? "Quote" : "Quotes");
    },

    // ===== CONFIRM BUTTON ENABLE STATE =====
    // Disable Print when nothing is checked. Single-quote
    // case is always enabled because there's nothing to
    // uncheck (the row renders without a checkbox at all).
    hasSelectedQuotesForPrint() {
      if (this.groupedOrders.length <= 1) return true;
      return this.selectedQuotesForPrint.length > 0;
    },
  },
  mounted() {
    if (localStorage.cartIdRFQ == null) {
      this.$router.push("/rfq/create");
    }
    this.getSuccessRFQ();
  },
  data() {
    return {
      orderId: "",
      configuredWear: true,
      companyName: "",
      // Print-header logo error fallback. Flipped true by
      // onPrintLogoError when the <img> fails to load,
      // swapping the markup to a text "STEELE" fallback
      // so the printed quote always carries a brand mark.
      printLogoFailed: false,
      billingCompanyName: "",
      billingStreet: "",
      billingStreet2: "",
      billingCity: "",
      billingState: "",
      billingPostalCode: "",
      shippingCompanyName: "",
      shippingStreet: "",
      shippingStreet2: "",
      shippingCity: "",
      shippingState: "",
      shippingPostalCode: "",
      orders: [],
      fullOrder: [],
      total: "",
      // Tracks which assembly cards have their parts table expanded.
      // Keyed by Quote Reference + machine/assembly key so state is
      // unique across cards. All assemblies start collapsed for a clean
      // overview-first view; click the summary row to drill in.
      expandedGroups: {},
      partsFields: [
        "item",
        "code",
        "description",
        { key: "qty", label: "QTY PER MACHINE" },
        "price",
        { key: "quoteqty", label: "QUOTED" },
        "cost",
      ],

      // ===== PRINT MODAL STATE =====
      // showPrintModal — drives the b-modal v-model. Flipped
      // true by openPrintModal (button click), back to false
      // by Cancel / dismiss / successful print.
      //
      // selectedQuotesForPrint — array of quote.number values
      // representing which quotes will appear in the print
      // output. Populated to "all of them" when the modal
      // opens; the user can deselect rows in the multi-quote
      // case. confirmPrint short-circuits if this is empty
      // (the modal's primary button is disabled in that
      // state, but we double-check).
      //
      // printingSelectedOnly — set true for the duration of
      // window.print(). The per-quote section wrapper hides
      // itself in print when this flag is true AND the
      // section's quote.number isn't in
      // selectedQuotesForPrint, via the print-quote-hidden
      // class plumbed through isQuoteHiddenForPrint(). Reset
      // to false in confirmPrint's finally so a stale flag
      // can't suppress a future full-page print.
      showPrintModal: false,
      selectedQuotesForPrint: [],
      printingSelectedOnly: false,
    };
  },
  methods: {
    // Build the two-level grouped structure consumed by the template.
    // Identical pattern to Cart and OrderDetails. Note: this page's API
    // returns `order.machine.description` (not `order.assembly.machine
    // .description` like the cart payload) and `order.assembly.name` —
    // resolved with optional chaining via the && idiom for safety. Parts
    // here are line items with the nested `lineItem.part.<field>` shape
    // (same as Cart, different from OrderDetails which is flat).
    buildGroupedOrders(orders) {
      var poGroups = {};

      (orders || []).forEach(function (order) {
        var quoteRef = order.number || '(no reference)';

        // --- Quote Reference level ---
        if (!poGroups[quoteRef]) {
          poGroups[quoteRef] = {
            number: order.number || '',
            cost: 0,
            _machineMap: {}
          };
        }
        var poGroup = poGroups[quoteRef];

        // --- Machine + Assembly level ---
        var machineName = (order.machine && order.machine.description) || '';
        var assemblyName = (order.assembly && order.assembly.name) || '';
        var mKey = machineName + '||' + assemblyName;

        if (!poGroup._machineMap[mKey]) {
          poGroup._machineMap[mKey] = {
            key: mKey,
            machineName: machineName,
            assemblyName: assemblyName,
            cost: 0,
            _partsMap: {}
          };
        }
        var mGroup = poGroup._machineMap[mKey];
        mGroup.cost += Number(order.cost) || 0;
        poGroup.cost += Number(order.cost) || 0;

        // --- Part dedup level ---
        // Keys on part code so duplicate part rows under the same
        // machine/assembly (across multiple backing PurchaseOrders
        // within the same Quote Reference) collapse into one row with
        // summed quantity and cost.
        (order.parts || []).forEach(function (lineItem) {
          if (!lineItem || !lineItem.part) return;
          var code =
            lineItem.part.code ||
            lineItem.part.item ||
            ('__id_' + lineItem.id);
          if (!mGroup._partsMap[code]) {
            mGroup._partsMap[code] = {
              id: lineItem.id,
              part: lineItem.part,
              quantity: Number(lineItem.quantity) || 0,
              cost: Number(lineItem.cost) || 0
            };
          } else {
            mGroup._partsMap[code].quantity += Number(lineItem.quantity) || 0;
            mGroup._partsMap[code].cost += Number(lineItem.cost) || 0;
          }
        });
      });

      return Object.keys(poGroups).map(function (poKey) {
        var po = poGroups[poKey];
        return {
          number: po.number,
          cost: po.cost,
          machineGroups: Object.keys(po._machineMap).map(function (mKey) {
            var mg = po._machineMap[mKey];
            return {
              key: mg.key,
              machineName: mg.machineName,
              assemblyName: mg.assemblyName,
              cost: mg.cost,
              mergedParts: Object.keys(mg._partsMap).map(function (k) {
                return mg._partsMap[k];
              })
            };
          })
        };
      });
    },

    // ===== ASSEMBLY EXPAND/COLLAPSE =====
    // Same pattern as Cart and OrderDetails. Each assembly card starts
    // collapsed so the user gets a clean overview (Quote Ref header +
    // chip-row summary + subtotal). Click the summary row (or its
    // toggle button) to reveal the parts table. Keyed by Quote Ref +
    // machine key so state is unique across cards.
    groupExpandKey(quoteRef, mgKey) {
      return quoteRef + '__' + mgKey;
    },
    isGroupExpanded(quoteRef, mgKey) {
      return !!this.expandedGroups[this.groupExpandKey(quoteRef, mgKey)];
    },
    toggleGroup(quoteRef, mgKey) {
      var k = this.groupExpandKey(quoteRef, mgKey);
      this.$set(this.expandedGroups, k, !this.expandedGroups[k]);
    },

    getSuccessRFQ() {
      apiService
        .createRFQ({
          id: this.$route.params.id,
          companyId: this.$route.params.companyId,
          purchaseOrders: this.$route.params.purchaseOrders,
          order: this.$route.params.order,
          SpecialInstructions: this.$route.params.SpecialInstructions,
          CreateQuote: true,
        })
        .then((request) => {
          this.orderSuccessful(request);
        })
        .catch((err) => this.orderFailed(err));
    },
    orderSuccessful(request) {
      delete localStorage.cartIdRFQ;
      delete localStorage.poNumberRFQ;
      delete localStorage.machineIdRFQ;
      (this.cartIdRFQ = request.id),
        (this.orderId = request.id),
        (this.orders = request.purchaseOrders),
        (this.companyName = request.companyName),
        (this.billingCompanyName = request.plantName),
        (this.billingStreet = request.plantStreet),
        (this.billingStreet2 = request.plantStreet2),
        (this.billingCity = request.plantCity),
        (this.billingState = request.plantState),
        (this.billingPostalCode = request.plantPostalCode),
        (this.shippingCompanyName = request.shipName),
        (this.shippingStreet = request.shipStreet),
        (this.shippingStreet2 = request.shipStreet2),
        (this.shippingCity = request.shipCity),
        (this.shippingState = request.shipState),
        (this.shippingPostalCode = request.shipPostalCode),
        (this.total = request.cost),
        (this.fullOrder = request);
      // Quote placed → send @jcsteele.com / non-Customer staff back to their NC999 home (Head handles the role
      // check + silent switch). No-op for Customers and for anyone already on NC999.
      if (this.$root) this.$root.$emit("jcs-return-home");
    },
    orderFailed(err) {
      this.$bvToast.toast("TraceID: " + err.error.response.data.traceId, {
        title: err.error.response.data.title,
        autoHideDelay: 10000,
        appendToast: false,
        variant: "danger",
        solid: true,
      });
    },
    // ===== PRINT MODAL: OPEN =====
    // Click handler for the .btn-print-order button. Opens the
    // confirmation modal (always — even for a single quote, so
    // the experience is consistent across counts). Pre-selects
    // every quote so "Print All" is the zero-click default;
    // the user only has to interact when they want a subset.
    //
    // Edge case: groupedOrders is empty (no orders loaded yet).
    // Surface a toast and skip opening the modal — matches the
    // pattern placeOrder uses for the same not-ready-yet
    // scenario.
    openPrintModal() {
      if (!this.groupedOrders || this.groupedOrders.length === 0) {
        this.$bvToast.toast(
          "Quote data isn't ready yet — please try again in a moment.",
          {
            title: "Cannot print",
            autoHideDelay: 4000,
            variant: "warning",
            solid: true,
          }
        );
        return;
      }
      // Reset selection to "everything checked" each time the
      // modal opens so a previous partial selection doesn't
      // linger. Quote refs are the source-of-truth identifier
      // for matching against the per-quote print sections.
      this.selectedQuotesForPrint = this.groupedOrders.map(function (g) {
        return g.number;
      });
      this.showPrintModal = true;
    },

    // ===== PRINT MODAL: CONFIRM =====
    // Triggered by the modal's primary button. Sets the
    // printingSelectedOnly flag (which the per-quote section
    // wrapper consumes via isQuoteHiddenForPrint), waits for
    // Vue to flush the class change to the DOM, then fires
    // window.print(). The browser's print dialog is modal — by
    // the time it returns we're safe to roll the flag back so
    // a subsequent unrelated print (e.g. browser hotkey) shows
    // the full page.
    //
    // Wrapped in try/finally so an exception during print
    // (rare, but possible — some embedded contexts disallow
    // it) doesn't strand the page in printingSelectedOnly=true
    // state, which would silently hide quote sections on
    // future prints.
    confirmPrint() {
      var self = this;
      // Defensive — ok-disabled covers the multi-quote empty
      // case, but this also gates accidental programmatic
      // calls.
      if (!this.hasSelectedQuotesForPrint) return;

      this.printingSelectedOnly = true;
      this.showPrintModal = false;

      // Wait for the section wrappers to pick up the
      // print-quote-hidden class via reactivity. Without
      // nextTick, window.print() runs against the pre-update
      // DOM and unselected quotes still appear.
      this.$nextTick(function () {
        try {
          window.print();
        } finally {
          // Reset on the NEXT tick so any frame the print
          // engine captures during a slow dialog open still
          // has the hidden state applied. window.print() is
          // synchronous in mainstream browsers, but the
          // belt-and-braces approach here costs nothing.
          self.$nextTick(function () {
            self.printingSelectedOnly = false;
          });
        }
      });
    },

    // ===== PRINT SELECTION HELPERS =====
    // isQuoteSelectedForPrint — checkbox v-bind helper.
    // isQuoteHiddenForPrint   — wrapper class helper. Returns
    //                           true when we're in selected-
    //                           only print mode AND this
    //                           quote isn't on the picked
    //                           list. The wrapper toggles
    //                           print-quote-hidden based on
    //                           the result; the @media print
    //                           rule does the actual hiding.
    // setQuoteSelectionForPrint — checkbox @change handler.
    // toggleSelectAllQuotesForPrint — Select / Deselect All
    //                                 link at the bottom of
    //                                 the modal.
    isQuoteSelectedForPrint(quoteRef) {
      return this.selectedQuotesForPrint.indexOf(quoteRef) !== -1;
    },
    isQuoteHiddenForPrint(quoteRef) {
      if (!this.printingSelectedOnly) return false;
      return !this.isQuoteSelectedForPrint(quoteRef);
    },
    setQuoteSelectionForPrint(quoteRef, checked) {
      var idx = this.selectedQuotesForPrint.indexOf(quoteRef);
      if (checked && idx === -1) {
        // Push a copy so v-model bindings further up see a
        // new array reference and re-render reliably (Vue 2
        // can miss in-place mutations of arrays held on
        // data props in some edge cases).
        this.selectedQuotesForPrint = this.selectedQuotesForPrint.concat([quoteRef]);
      } else if (!checked && idx !== -1) {
        var next = this.selectedQuotesForPrint.slice();
        next.splice(idx, 1);
        this.selectedQuotesForPrint = next;
      }
    },
    toggleSelectAllQuotesForPrint() {
      // If everything is currently checked, clear; otherwise
      // select all. Mirrors the Finalize modal's same toggle.
      if (this.selectedQuotesForPrint.length === this.groupedOrders.length) {
        this.selectedQuotesForPrint = [];
      } else {
        this.selectedQuotesForPrint = this.groupedOrders.map(function (g) {
          return g.number;
        });
      }
    },

    // Logo <img> in the print header failed to load —
    // flip the flag so the v-if swaps to the text
    // fallback ("STEELE") visible in the next paint,
    // before the user triggers print. Shared across every
    // per-quote header instance — one error event flips
    // the flag for all of them, which is what we want
    // since they all reference the same asset.
    onPrintLogoError() {
      this.printLogoFailed = true;
    },
    // "Place Order" — converts this saved quote into an actual order
    // cart. Pushes to the Cart route with the same purchase-order
    // payload, which the Cart page persists as an order via its own
    // saveCart flow. Mirrors the createOrder() pattern from
    // RFQSavedItems.vue. The cartIdRFQ key was already cleaned up in
    // orderSuccessful — we set localStorage.cartId so the order side
    // picks it up. If fullOrder hasn't loaded yet (rare), surface a
    // toast so the user knows to retry rather than silently no-op.
    placeOrder() {
      if (!this.fullOrder || !this.orderId) {
        this.$bvToast.toast(
          "Quote data isn't ready yet — please try again in a moment.",
          {
            title: "Cannot place order",
            autoHideDelay: 4000,
            variant: "warning",
            solid: true,
          }
        );
        return;
      }
      localStorage.cartId = this.orderId;
      delete localStorage.cartIdRFQ;

      this.$router.push({
        name: "Cart",
        params: {
          cartId: this.orderId,
          order: this.fullOrder,
          purchaseOrders: this.fullOrder.purchaseOrders,
          companyId: localStorage.companyId,
          fullOrder: this.fullOrder,
        },
      });
    },
  },
};
</script>

<style scoped>
/* ===== PRINT-ONLY HEADER (HIDDEN ON SCREEN) =====
   The letterhead-style banner at the top of the template
   appears ONLY in print output. Hidden by default here so
   it doesn't show in the live UI; revealed by the @media
   print rule in the non-scoped style block at the bottom
   of this file. */
.print-only-header {
    display: none;
}

/* ===== PO/QUOTE GROUP (the grouping container) =====
   Same visual style as Cart and OrderDetails — light gray panel with
   a red left accent — so a customer reviewing a saved quote sees the
   same structure they used while building it. */
.po-group {
    background: #fafafa;
    border: 1px solid #e8e8e8;
    border-left: 3px solid #c0392b;
    border-radius: 8px;
    margin: 0 0 28px;
    padding: 24px 22px 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

/* Sequential quote header. Quote Reference itself shows on the subtotal
   row inside the first assembly card. */
.order-header {
    display: flex;
    align-items: baseline;
    gap: 12px;
    margin-bottom: 12px;
    padding-bottom: 10px;
    border-bottom: 1px dashed #d8d8d8;
}

.order-header-label {
    font-size: 20px;
    font-weight: 700;
    color: #c0392b;
    letter-spacing: 0.3px;
}

.order-header-meta {
    font-size: 12px;
    color: #777;
    font-style: italic;
}

/* ===== ASSEMBLY CARD (inner blocks within a quote group) ===== */
.assembly-card {
    background: #fff;
    border: 1px solid #ececec;
    border-radius: 6px;
    padding: 18px 20px 14px;
    margin-bottom: 16px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}

.assembly-card:last-of-type {
    margin-bottom: 18px;
}

.assembly-card-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 14px;
}

.assembly-card-heading {
    flex: 1 1 auto;
    min-width: 200px;
}

/* Quote-ref subtotal block on the FIRST assembly card per quote group,
   right-aligned in the card header so it shares a row with the assembly
   title. Read-only here (no inline edit) since the quote is finalized. */
.assembly-card-po-subtotal {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 14px;
    background: #fafafa;
    border: 1px solid #e8e8e8;
    border-radius: 6px;
    padding: 8px 14px;
}

.assembly-card-po-label {
    font-size: 12px;
    color: #555;
    white-space: nowrap;
}

.assembly-card-po-amount {
    font-size: 16px;
    font-weight: 700;
    color: #c0392b;
    white-space: nowrap;
    letter-spacing: 0.3px;
}

@media (max-width: 767.98px) {
    .assembly-card-header {
        flex-direction: column;
    }
    .assembly-card-po-subtotal {
        align-self: stretch;
        justify-content: space-between;
    }
}

.assemblyName {
    font-size: 20px;
    font-weight: 700;
    color: #333;
    margin-bottom: 2px;
}

.assemblyDescription {
    font-size: 13px;
    color: #777;
}

.subtotal-po {
    color: #c0392b;
    font-weight: 700;
}

/* ===== COLLAPSIBLE ASSEMBLY SUMMARY ROW =====
   Click anywhere on the row to toggle the parts table below it. Shows
   chips with code × qty for an at-a-glance view when collapsed. */
.assembly-summary {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 10px 12px 10px 10px;
    background: #fafafa;
    border: 1px solid #eee;
    border-radius: 6px;
    cursor: pointer;
    margin-top: 4px;
    transition: background 0.15s ease, border-color 0.15s ease;
}

.assembly-summary:hover {
    background: #f3f3f3;
    border-color: #e0e0e0;
}

.assembly-summary.expanded {
    background: #fff;
    border-color: #ddd;
}

.assembly-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    min-width: 30px;
    border-radius: 50%;
    border: 1.5px solid #c0392b;
    background: #fff;
    color: #c0392b;
    font-size: 20px;
    font-weight: 700;
    line-height: 1;
    flex-shrink: 0;
    transition: background 0.15s ease, color 0.15s ease, transform 0.15s ease;
}

.assembly-summary:hover .assembly-toggle {
    background: #c0392b;
    color: #fff;
}

.assembly-toggle.expanded {
    background: #333;
    border-color: #333;
    color: #fff;
}

.assembly-summary:hover .assembly-toggle.expanded {
    background: #555;
    border-color: #555;
}

.assembly-toggle-icon {
    display: block;
    line-height: 1;
    margin-top: -2px;
}

.assembly-summary-content {
    flex: 1 1 auto;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    min-width: 0;
}

.assembly-summary-count {
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #888;
    margin-right: 4px;
}

.code-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 14px;
    font-size: 12px;
    font-weight: 600;
    color: #333;
    white-space: nowrap;
}

.code-chip .code-qty {
    color: #c0392b;
    font-weight: 700;
    font-size: 12px;
}

.assembly-summary-empty {
    font-size: 13px;
    color: #999;
    font-style: italic;
}

.assembly-summary-total {
    flex-shrink: 0;
    font-size: 16px;
    font-weight: 700;
    color: #c0392b;
    min-width: 100px;
    text-align: right;
}

.assembly-table-wrap {
    margin-top: 12px;
    padding-top: 8px;
    border-top: 1px solid #f0f0f0;
}

/* ===== QUOTE GRAND TOTAL =====
   Mirrors Cart.vue and RFQSavedItems.vue: clean white card with brand-
   red value and a black top accent. Visually anchors the bottom of
   the page where the old dark-image banner used to be. */
.cart-grand-total {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 18px 20px;
    margin-top: 8px;
    margin-bottom: 16px;
    background: #fff;
    border: 1px solid #e5e5e5;
    border-top: 3px solid #222;
    border-radius: 6px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.cart-grand-total-label {
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: #333;
}

.cart-grand-total-value {
    font-size: 22px;
    font-weight: 700;
    color: #c0392b;
    letter-spacing: 0.3px;
}

@media (max-width: 575.98px) {
    .cart-grand-total {
        padding: 14px 16px;
    }
    .cart-grand-total-value {
        font-size: 18px;
    }
}

/* ===== FINALIZE ACTIONS =====
   Four primary buttons sitting horizontal, beneath the grand total:
   "Place Order" (red, primary commit) plus three relocated nav
   buttons (Check Order Status / Create New Order / Create New Quote,
   all dark / secondary). On viewports < 768px they wrap so each
   button stays readable. */
.cart-finalize-actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: stretch;
    gap: 12px;
    margin-bottom: 24px;
}

.cart-finalize-actions .btn {
    flex: 1 1 180px;
    min-width: 160px;
    max-width: 240px;
    padding: 10px 18px;
    font-size: 13px;
    letter-spacing: 0.4px;
    text-align: center;
}

@media (max-width: 575.98px) {
    .cart-finalize-actions .btn {
        flex-basis: 100%;
        max-width: none;
    }
}

/* ===== PRINT QUOTE BAND =====
   Centered Print button in its own breathing-room band.
   Same styling as the Order pages so the two flows feel
   consistent. Brand red default, black on hover. Replaces
   the legacy "Download PDF Version of Your Quote" anchor
   link that lived inside #finalizeTop. */
.print-order-band {
    padding: 32px 0;
    text-align: center;
    background: transparent;
}

.btn-print-order {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 10px 24px;
    border-radius: 6px;
    background: #c0392b;
    border: 1px solid #c0392b;
    color: #fff;
    transition: background 200ms ease, color 200ms ease,
                border-color 200ms ease, box-shadow 200ms ease;
}

.btn-print-order:hover,
.btn-print-order:focus {
    background: #000;
    color: #fff;
    border-color: #000;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.btn-print-icon {
    display: inline-block;
}

/* ===== PER-QUOTE PRINT SECTION =====
   Wrapper around each quote group that gives the @media
   print rules a hook to (a) page-break between consecutive
   quotes and (b) hide a section entirely when the user
   picked a subset to print. On screen it's a transparent
   passthrough — no padding, no border, no visual change. */
.print-quote-section {
    /* Intentionally empty on screen — display:block default
       with no styling. Print rules at the bottom of this
       file do all the work. */
}

/* ===== PRINT QUOTE MODAL =====
   Modal-scoped styles for #print-quote-modal, mirroring the
   visual treatment of the Finalize / Print and Modify Quote
   modals on /cart/rfqsaveditems so the two pages feel like
   siblings. The .modify-restored-banner / .convert-* class
   names are reused verbatim from RFQSavedItems' modal so any
   future tweaks to the picker styling there propagate here
   without divergence — the styles below scope them to
   .print-quote-modal so they don't leak globally. */
.print-quote-modal .modify-restored-banner.print-quote-banner {
    background: linear-gradient(135deg, #fff5f3 0%, #fff 100%);
    border: 2px solid #c0392b;
    border-radius: 10px;
    padding: 18px 22px;
    margin: 0 0 18px;
    text-align: center;
    box-shadow: 0 4px 16px rgba(192, 57, 43, 0.12),
                0 1px 3px rgba(0, 0, 0, 0.04);
}

.print-quote-modal .modify-restored-title {
    font-size: 20px;
    font-weight: 800;
    color: #c0392b;
    letter-spacing: 0.3px;
    margin-bottom: 6px;
    line-height: 1.2;
}

.print-quote-modal .modify-restored-body {
    font-size: 13px;
    color: #444;
    line-height: 1.5;
}

.print-quote-modal .modify-restored-body strong {
    color: #222;
    font-weight: 700;
}

@media (max-width: 575.98px) {
    .print-quote-modal .modify-restored-title {
        font-size: 18px;
    }
    .print-quote-modal .modify-restored-banner.print-quote-banner {
        padding: 14px 16px;
    }
}

/* Modal body intro line under the banner. Same sizing as
   the convert / save-for-later modals. */
.print-quote-modal .convert-modal-intro {
    margin: 0 0 14px;
    font-size: 13px;
    color: #555;
}

/* Quote pick rows. Container holds N rows, each rendering
   either a b-form-checkbox (multi-quote) or a static
   summary block (single-quote). Brand-red left border on
   the row treatment ties it to the on-page Quote # group
   styling. */
.print-quote-modal .convert-quote-list {
    margin: 0 0 12px;
    padding: 0;
}

.print-quote-modal .convert-quote-row {
    padding: 10px 12px;
    background: #fafafa;
    border: 1px solid #ececec;
    border-radius: 6px;
    margin-bottom: 6px;
}

.print-quote-modal .convert-quote-row:last-child {
    margin-bottom: 0;
}

.print-quote-modal .convert-quote-row-grouped {
    border-left: 3px solid #c0392b;
}

.print-quote-modal .convert-quote-row .custom-control-label {
    cursor: pointer;
    width: 100%;
}

.print-quote-modal .convert-quote-row-main {
    display: flex;
    align-items: baseline;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 2px;
}

.print-quote-modal .convert-quote-row-label {
    font-size: 14px;
    font-weight: 700;
    color: #c0392b;
    letter-spacing: 0.3px;
}

.print-quote-modal .convert-quote-row-meta {
    font-size: 12px;
    color: #555;
    font-weight: 400;
}

.print-quote-modal .convert-quote-row-meta strong {
    color: #222;
    font-weight: 700;
}

.print-quote-modal .convert-quote-row-sub {
    font-size: 11px;
    color: #888;
    letter-spacing: 0.2px;
    text-transform: uppercase;
}

.print-quote-modal .convert-quote-row-amount {
    color: #c0392b;
    font-weight: 700;
}

/* Per-assembly mini-list inside each row — one line per
   (machine, assembly, part-count) tuple, indented and
   smaller so it reads as supplementary detail beneath the
   group's primary label. Mirrors the layout used by the
   Finalize modal on /cart/rfqsaveditems. */
.print-quote-modal .convert-quote-row-assemblies {
    margin-top: 4px;
    font-size: 11px;
    color: #666;
    line-height: 1.4;
}

.print-quote-modal .convert-quote-row-assembly {
    padding: 1px 0;
}

/* Single-quote case: no checkbox, just the same row
   layout as the multi-quote case minus the form control.
   .print-quote-row-static is the wrapper that replaces
   b-form-checkbox in that branch. */
.print-quote-modal .print-quote-row-static {
    padding: 0;
}

/* Select All / Deselect All link, right-aligned beneath
   the row list. Brand red, small caps treatment to match
   the Finalize modal. */
.print-quote-modal .convert-quote-toggle-all {
    text-align: right;
    margin: 0 0 4px;
}

.print-quote-modal .convert-quote-toggle-all a {
    font-size: 11px;
    font-weight: 700;
    color: #c0392b;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    cursor: pointer;
    text-decoration: none;
}

.print-quote-modal .convert-quote-toggle-all a:hover {
    text-decoration: underline;
}
</style>

<!-- ============================================================
     PRINT STYLESHEET (non-scoped, global @media print)
     ============================================================
     Same pattern as OrderDetails.vue / Success.vue. Hides
     the page chrome (header / breadcrumbs / footer / nav),
     the action buttons (Place Order / Check Status / Create
     New Order / Create New Quote), and the Print Quote band
     itself. Reveals the print-only letterhead header at the
     top. Forces every quote assembly's parts table expanded
     regardless of the user's collapse state, so the printed
     receipt shows all line items. Tightens typography for
     density. -->
<style>
@media print {
    /* Hide chrome — multiple selector forms with elevated
       specificity to win against any global stylesheet
       rules with !important on these elements. */
    body #breadcrumb,
    body #breadcrumb *,
    div#breadcrumb,
    div#breadcrumb *,
    [id="breadcrumb"],
    [id="breadcrumb"] *,
    [class*="breadcrumb" i],
    [class*="breadcrumb" i] *,
    [id*="breadcrumb" i],
    [id*="breadcrumb" i] *,
    nav[aria-label*="breadcrumb" i],
    nav[aria-label*="Breadcrumb"],
    ol.breadcrumb,
    ul.breadcrumb,
    body header,
    body .header,
    body #header,
    body [role="banner"],
    body nav.navbar,
    body .navbar,
    body footer,
    body .footer,
    body #footer,
    body #finalizeTop,
    .no-print,
    [class*="userway"],
    [id*="userway"],
    [class*="accessibility-widget"],
    [class*="translate-widget"],
    [id*="google_translate"],
    iframe {
        display: none !important;
        visibility: hidden !important;
        height: 0 !important;
        overflow: hidden !important;
        margin: 0 !important;
        padding: 0 !important;
    }

    /* Strip the brushed-steel body background. */
    html,
    body {
        background: #fff !important;
        background-image: none !important;
    }

    /* ===== STRIP TOP-OF-PAGE WHITESPACE =====
       App.vue sets a dynamic padding-top on body to clear
       the fixed site header. That padding has no purpose
       in print (header is hidden) but the inline style
       stays and creates a tall blank band at page top.
       Force it to zero. */
    html,
    body,
    #app,
    #app > div,
    main,
    .main,
    [role="main"],
    .container-fluid,
    #savedRFQ {
        margin-top: 0 !important;
        padding-top: 0 !important;
    }
    body[style] {
        padding-top: 0 !important;
        margin-top: 0 !important;
    }

    /* Show the print-only header. Hidden on screen by the
       scoped rule above; revealed in print here. */
    .print-only-header {
        display: flex !important;
        align-items: flex-start;
        justify-content: space-between;
        padding: 0 5px 16px;
        margin: 0 0 16px;
        border-bottom: 2px solid #c0392b;
    }

    .print-only-header-logo {
        flex: 0 0 auto;
        margin-left: 5px;
    }

    .print-only-header-logo-img {
        width: 180px;
        max-width: 180px;
        height: auto;
        max-height: 80px;
        display: block;
    }

    /* Text fallback used when the SVG <img> fails to load. */
    .print-only-header-logo-text {
        font-size: 32px;
        font-weight: 900;
        letter-spacing: 1px;
        color: #c0392b;
        line-height: 1;
        font-family: Arial, Helvetica, sans-serif;
    }

    .print-only-header-address {
        flex: 0 0 auto;
        text-align: right;
        font-size: 11px;
        line-height: 1.4;
        color: #222;
    }

    .print-only-header-address-name {
        font-weight: 700;
        font-size: 12px;
        margin-bottom: 2px;
    }

    /* Doc identifier line above the Parts Department block.
       Brand-red + larger weight so it reads as the headline
       of the printed page — recipient can tell at a glance
       which Order/Quote/Acknowledgement they're looking at.
       Color-adjust hint forces the red through browsers'
       default print color-strip (which the rest of this
       stylesheet already uses on red elements). */
    .print-only-header-doc-id {
        font-weight: 700;
        font-size: 14px;
        color: #c0392b;
        margin-bottom: 6px;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
    }

    /* ===== INPUT-AS-TEXT IN PRINT =====
       Disabled <input> elements (e.g. the locked CART QTY
       input on Success.vue's parts table) carry intrinsic
       browser-default widths that override table-cell
       sizing and push other columns out of alignment with
       the header row. Strip the input chrome and intrinsic
       width so the value renders inline as text — same
       width as the other plain-text cells in the column.
       Defensive on files that don't currently use inputs in
       tables; harmless if no inputs are present. */
    .assembly-table-wrap input[disabled],
    .assembly-table-wrap input[readonly],
    .POOrders input[disabled],
    .POOrders input[readonly],
    .success-qty,
    .success-qty input {
        border: 0 !important;
        background: transparent !important;
        padding: 0 !important;
        margin: 0 !important;
        width: auto !important;
        max-width: none !important;
        min-width: 0 !important;
        display: inline !important;
        -webkit-appearance: none !important;
        appearance: none !important;
        color: inherit !important;
        font: inherit !important;
        box-shadow: none !important;
        outline: 0 !important;
    }

    /* ===== TIGHT TYPOGRAPHY FOR PRINT =====
       Same density as OrderDetails so a quote fits in as
       few pages as possible. */
    body {
        font-size: 10px !important;
        line-height: 1.35 !important;
    }
    #savedRFQ h1 {
        font-size: 18px !important;
        margin: 0 0 4px !important;
    }
    #savedRFQ h2 {
        font-size: 13px !important;
        margin: 0 0 4px !important;
    }
    #savedRFQ h4 {
        font-size: 11px !important;
        margin: 0 0 4px !important;
    }
    #savedRFQ p {
        font-size: 10px !important;
        line-height: 1.35 !important;
        margin: 0 0 2px !important;
    }
    #finalizeTop {
        padding-top: 8px !important;
        padding-bottom: 8px !important;
    }
    #finalizeTop .container {
        padding-top: 0 !important;
        padding-bottom: 0 !important;
    }
    #finalizeTop .row {
        margin-bottom: 8px !important;
    }
    #finalizeTop .row.mb-5 {
        margin-bottom: 8px !important;
    }
    .po-group {
        margin-top: 6px !important;
        margin-bottom: 6px !important;
        padding: 6px 8px !important;
    }
    .order-header {
        margin-bottom: 4px !important;
        padding-bottom: 4px !important;
    }
    .order-header-label {
        font-size: 11px !important;
    }
    .order-header-meta {
        font-size: 9px !important;
    }
    .assembly-card {
        margin-bottom: 4px !important;
        padding: 6px 8px !important;
    }
    .assemblyName {
        font-size: 10px !important;
        margin: 0 !important;
    }
    .assemblyDescription {
        font-size: 9px !important;
        margin: 0 !important;
    }
    .assembly-card-po-label {
        font-size: 9px !important;
    }
    .assembly-card-po-amount {
        font-size: 10px !important;
    }
    .assembly-summary {
        padding: 4px 0 !important;
        margin: 0 !important;
    }
    .assembly-summary-count,
    .code-chip,
    .code-qty {
        font-size: 9px !important;
    }
    .assembly-summary-total {
        font-size: 10px !important;
    }
    .assembly-table-wrap {
        margin: 4px 0 0 !important;
    }
    .assembly-table-wrap table {
        font-size: 9px !important;
        /* User feedback: in print the columns were squished
           into the left ~60% of the page, leaving a wide
           margin of empty space on the right. Forcing the
           table to span the full content width spreads the
           columns evenly across the page so the data uses
           the available real estate. */
        width: 100% !important;
        table-layout: auto !important;
    }
    .assembly-table-wrap th,
    .assembly-table-wrap td {
        /* Padding bumped from 3px/5px to 4px/8px so cells
           breathe a bit more horizontally now that the table
           occupies full page width. */
        padding: 4px 8px !important;
        font-size: 9px !important;
    }
    .cart-grand-total {
        margin-top: 8px !important;
        padding: 8px 12px !important;
    }
    .cart-grand-total-label {
        font-size: 10px !important;
    }
    .cart-grand-total-value {
        font-size: 14px !important;
    }

    /* Page break control — only on small atoms, not on the
       wide po-group (which can force whole-block page-shoves
       and leave white gaps). */
    .info-card,
    .cart-grand-total,
    .assembly-card {
        page-break-inside: avoid;
        break-inside: avoid;
    }

    /* ===== FORCE-EXPAND COLLAPSED ASSEMBLIES =====
       v-show writes inline display:none when collapsed;
       override with !important to show every parts table
       in print regardless of expand/collapse state. */
    .assembly-table-wrap {
        display: block !important;
    }
    .assembly-table-wrap[style*="display: none"],
    .assembly-table-wrap[style*="display:none"] {
        display: block !important;
    }
    /* Hide the +/− toggle in print since the section it
       controls is always visible now. */
    .assembly-toggle {
        display: none !important;
    }

    /* b-table stacked-mode override — print viewport trips
       the lg breakpoint and would stack each cell vertically. */
    .b-table.b-table-stacked-lg > tbody > tr,
    .b-table.b-table-stacked-md > tbody > tr,
    .b-table.b-table-stacked-sm > tbody > tr {
        display: table-row !important;
    }
    .b-table.b-table-stacked-lg > tbody > tr > td,
    .b-table.b-table-stacked-md > tbody > tr > td,
    .b-table.b-table-stacked-sm > tbody > tr > td {
        display: table-cell !important;
    }
    .b-table.b-table-stacked-lg > thead,
    .b-table.b-table-stacked-md > thead,
    .b-table.b-table-stacked-sm > thead {
        display: table-header-group !important;
    }
    .b-table.b-table-stacked-lg > tbody > tr > [data-label]::before,
    .b-table.b-table-stacked-md > tbody > tr > [data-label]::before,
    .b-table.b-table-stacked-sm > tbody > tr > [data-label]::before {
        display: none !important;
    }

    /* ===== PER-QUOTE PAGE BREAKS =====
       Each .print-quote-section past the first gets a
       page-break-before so every Quote # block starts on
       its own printed page with its own letterhead at the
       top. The first section inherits the page-top
       position naturally — no break needed there. */
    .print-quote-section + .print-quote-section {
        page-break-before: always;
        break-before: page;
    }

    /* Also keep each section internally cohesive — avoid
       the rare case where a group splits awkwardly across
       a page boundary mid-assembly. Inner .assembly-card
       already has page-break-inside: avoid above for the
       same reason at a finer grain. */
    .print-quote-section {
        page-break-inside: auto;
        break-inside: auto;
    }

    /* ===== SELECTIVE HIDE FOR SUBSET PRINTING =====
       confirmPrint() flips printingSelectedOnly=true and
       Vue toggles `print-quote-hidden` onto every section
       whose quote.number isn't in selectedQuotesForPrint.
       The display: none here removes those sections from
       the printed output entirely — including their
       per-quote letterhead — so a 3-of-5 print only emits
       the picked 3 with correct page breaks between them.
       The companion `+` selector above already handles
       page-break-before on whichever section ends up
       second in the printed sequence. */
    .print-quote-hidden,
    .print-quote-hidden * {
        display: none !important;
        visibility: hidden !important;
        height: 0 !important;
        overflow: hidden !important;
        margin: 0 !important;
        padding: 0 !important;
    }

    @page {
        margin: 0.5in;
    }

    /* ===== FORCE DARK TEXT FOR PRINT READABILITY =====
       Browser print engines lighten text by default to save
       ink. Combined with the various screen-only light-gray
       colors (#555, #666, #777, #888, #aaa) sprinkled across
       the screen styles, the printed output ends up muted
       and hard to read. Force every text node to full black
       AND bump font-weight slightly so thin glyphs don't
       disappear at small print font sizes. */
    *,
    *::before,
    *::after {
        color: #000 !important;
    }

    body,
    body p,
    body span,
    body td,
    body th,
    body div {
        font-weight: 500 !important;
    }

    /* ===== AGGRESSIVE GLOBAL DECORATIVE RESET =====
       Earlier attempts to strip borders only on specific
       containers didn't eliminate the vertical gray lines
       running down the body of printed pages. Edge browser
       in particular prints subtle borders by default —
       different browsers behave differently here.

       Brute-force approach: wipe ALL backgrounds, borders,
       and shadows on EVERY element AND every pseudo-element.
       The pseudo-element coverage matters — App.vue (or a
       global stylesheet) sets up `body::before` and
       `body::after` to render the brushed-steel side rails
       around the page, and a universal `*` selector by
       itself doesn't catch those. Without this rule the
       rails stay visible in print and produce vertical bars
       running the full page height.

       border-color: transparent (rather than border-width:
       0) preserves layout — elements that used borders for
       spacing keep their box geometry, only the visible
       line goes away. */
    *,
    *::before,
    *::after {
        background: transparent !important;
        background-color: transparent !important;
        background-image: none !important;
        border-color: transparent !important;
        box-shadow: none !important;
        outline: none !important;
    }

    /* Explicit body pseudo-element kill — App.vue's brushed-
       steel side rails attach here. Belt-and-suspenders with
       the universal rule above; some browsers need the
       targeted nudge to actually drop the rendered content. */
    body::before,
    body::after,
    html::before,
    html::after {
        display: none !important;
        content: none !important;
    }

    /* ===== HIDE FLOATING UTILITY TOOLBAR =====
       Head.vue mounts a fixed-position #utility-toggle-stack
       in the bottom-right of every page, containing three
       toggles: Report-a-Bug, font-size (Aa), and translate
       (globe). These are screen-only UI affordances; in a
       printed receipt they're noise. */
    #utility-toggle-stack,
    #utility-toggle-stack *,
    .util-toggle,
    .util-toggle-bug,
    .util-toggle-font,
    .util-toggle-translate,
    .util-icon,
    .util-label,
    .util-close,
    .util-body,
    #google-translate-floating,
    #google_translate_element {
        display: none !important;
        visibility: hidden !important;
        height: 0 !important;
        width: 0 !important;
        overflow: hidden !important;
    }

    /* Hide any horizontal-rule elements which would otherwise
       print as visible divider lines. */
    hr {
        display: none !important;
    }

    /* ===== RESTORE INTENTIONAL CHROME =====
       Now re-establish the visual elements that DO belong
       in print:
         - Data table cell borders carry data structure
         - print-only-header gets its red bottom rule (the
           branded divider between header and content)
         - Red brand accents stay red */
    table,
    .assembly-table-wrap table {
        border-collapse: collapse !important;
    }
    th,
    td,
    .assembly-table-wrap th,
    .assembly-table-wrap td {
        border: 1px solid #888 !important;
    }

    .print-only-header {
        border-bottom: 2px solid #c0392b !important;
    }

    /* Targeted color preservation on the red brand elements
       so they print in their declared color instead of the
       browser's default auto-strip. */
    .print-only-header,
    .print-only-header *,
    .print-only-header-logo-img,
    .print-only-header-logo-text,
    .print-only-header-logo-text *,
    [class*="-red"],
    [class*="brand-red"] {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
    }

    /* Restore red on the brand wordmark fallback + any
       explicit red accents the page intentionally carries.
       Order matters — these rules must come after the
       universal `*` color override above. */
    .print-only-header-logo-text,
    .print-only-header-logo-text *,
    [class*="-red"],
    [class*="brand-red"] {
        color: #c0392b !important;
    }
}
</style>